import React, { useState } from "react";
import { Flag } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";

export function ReportDialog({ targetType, targetId, label = "Report" }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);

  const authed = user && typeof user === "object";

  const submit = async () => {
    if (!reason.trim()) {
      toast.error("Please describe the issue.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/reports", {
        target_type: targetType,
        target_id: targetId,
        reason: reason.trim(),
      });
      toast.success(data.message);
      setOpen(false);
      setReason("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (!authed) {
    return (
      <button
        onClick={() => navigate("/login")}
        className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-400 hover:text-red-500"
        data-testid="report-login-prompt"
      >
        <Flag className="h-3.5 w-3.5" /> {label}
      </button>
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-400 hover:text-red-500"
          data-testid="report-trigger"
        >
          <Flag className="h-3.5 w-3.5" /> {label}
        </button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Report this {targetType}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-500">
          Tell us what's wrong. Our moderators will review this content.
        </p>
        <Textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Describe the issue (e.g. inaccurate information, inappropriate content)..."
          className="min-h-[100px]"
          data-testid="report-reason-input"
        />
        <DialogFooter>
          <button onClick={submit} disabled={loading} className="btn-navy" data-testid="report-submit-btn">
            {loading ? "Submitting..." : "Submit report"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ReportDialog;
